import { useState } from 'react';
import Axios, { CancelTokenSource, AxiosError } from 'axios';
import AxiosHelper from 'services/AxiosHelper';
import { FormValidationPromise } from 'shared/types';
import { handleAxiosErrorWithValidation } from 'shared/util/Misc';
import { Localizer } from 'services/Localizer';
import { MY_ACCOUNT_SHOPPING_LISTS_URL } from 'features/my-account/routes.commerce';
import { AddToListProps, BulkAddToListProps, ShoppingListDto } from './types';
import { AddToListResponse, BulkAddToListResponse } from 'services/UserData.Commerce';

export interface ShoppingListsState {
	isSaving: boolean;
	shoppingListBeingEdited: ShoppingListDto | null;
	shoppingList: ShoppingListDto | null;
	error: Error | AxiosError | null;
	errorMessage: string | null;
	isDeleting: boolean;
}

export interface ShoppingListsActor {
	beginCreateShoppingList: () => void;
	beginEditShoppingList: (shopingList: ShoppingListDto) => void;
	saveShoppingList: (formValues: ShoppingListDto) => FormValidationPromise<void>;
	stopEditingShoppingList: () => void;
	addProductToList: (props: AddToListProps) => Promise<AddToListResponse | null>;
	bulkAddProductToList: (props: BulkAddToListProps) => Promise<BulkAddToListResponse | null>;
	removeShoppingList: (shoppingList: ShoppingListDto) => Promise<void>;
}

export interface UseShoppingLists {
	state: ShoppingListsState;
	actor: ShoppingListsActor;
}

export function useShoppingLists(): UseShoppingLists {
	const [shoppingListBeingEdited, setShoppingListBeingEdited] = useState<ShoppingListDto | null>(null);
	const [error, setError] = useState<Error | AxiosError | null>(null);
	const [errorMessage, setErrorMessage] = useState<string | null>(null);
	const [isSaving, setIsSaving] = useState(false);
	const [shoppingList, setShoppingList] = useState<ShoppingListDto | null>(null);
	const [isDeleting, setIsDeleting] = useState(false);

	/**
	 * Begin creating an shopping list
	 */
	const beginEditShoppingList = (shoppingList: ShoppingListDto) => {
		setShoppingListBeingEdited(shoppingList);
	};

	const beginCreateShoppingList = () => {
		setShoppingListBeingEdited({} as ShoppingListDto);
	};

	const stopEditingShoppingList = () => {
		setError(null);
		setShoppingListBeingEdited(null);
	};

	/**
	 * Creates an shopping list associated to the current user on the server
	 */
	const saveShoppingList = async (formValues: ShoppingListDto): FormValidationPromise<void> => {
		// sanity check
		if (!shoppingListBeingEdited) {
			return Promise.resolve();
		}

		setIsSaving(true);

		const requestToken: CancelTokenSource = Axios.CancelToken.source();

		const shoppingList: ShoppingListDto = {
			...formValues,
		};

		try {
			const response = await AxiosHelper.post<ShoppingListDto>(
				`/ajax${MY_ACCOUNT_SHOPPING_LISTS_URL}/edit`,
				shoppingList,
				{
					cancelToken: requestToken.token,
				},
			);

			setShoppingListBeingEdited(null);
			setShoppingList(response.data);
			setError(null);
		} catch (error) {
			if (Axios.isCancel(error)) {
				// if the component was unmounted, there's nothing to do
				return;
			}

			setError(error);

			return handleAxiosErrorWithValidation(error, Localizer('SaveErrorMessage'));
		} finally {
			setIsSaving(false);
		}
	};

	const addProductToList = async (props: AddToListProps) => {
		setErrorMessage(null);
		setError(null);

		try {
			const response = await AxiosHelper.post<AddToListResponse>(
				`/ajax/shopping-lists/${props.shoppingListId}/products/${props.itemId}`,
				{
					quantity: props.quantity,
					productPricingLineId: props.productPricingLineId,
				},
			);
			return response.data;
		} catch (error) {
			setError(error);

			console.warn('Error when adding product to list', error);

			const axiosError = error as AxiosError;

			if (axiosError && axiosError.response && axiosError.response.data) {
				if (axiosError.response.data.message) {
					setErrorMessage(axiosError.response.data.message);
				}
			} else {
				throw error;
			}

			return null;
		}
	};

	const bulkAddProductToList = async (props: BulkAddToListProps) => {
		setErrorMessage(null);
		setError(null);

		try {
			const response = await AxiosHelper.post<BulkAddToListResponse>(
				`/ajax/shopping-lists/${props.shoppingListId}`,
				{
					items: props.items,
				},
			);
			return response.data;
		} catch (error) {
			setError(error);

			console.warn('Error when adding products to list', error);

			const axiosError = error as AxiosError;

			if (axiosError && axiosError.response && axiosError.response.data) {
				if (axiosError.response.data.message) {
					setErrorMessage(axiosError.response.data.message);
				}
			} else {
				throw error;
			}

			return null;
		}
	};

	const removeShoppingList = async (shoppingList: ShoppingListDto): Promise<void> => {
		const requestToken: CancelTokenSource = Axios.CancelToken.source();

		setIsDeleting(true);
		try {
			const response = await AxiosHelper.delete(`/ajax/shopping-lists/${shoppingList.id}`, {
				cancelToken: requestToken.token,
			});

			if (response) {
				document.location.href = MY_ACCOUNT_SHOPPING_LISTS_URL;
			}
		} catch (error) {
			if (Axios.isCancel(error)) {
				// if the component was unmounted, there's nothing to do
				return;
			}

			setError(error);
			console.error('An error occurred while deleting.', error);
		} finally {
			setIsDeleting(false);
		}
	};

	return {
		state: {
			isSaving,
			error: error,
			shoppingListBeingEdited,
			shoppingList: shoppingList,
			errorMessage: errorMessage,
			isDeleting,
		},
		actor: {
			beginCreateShoppingList,
			beginEditShoppingList,
			saveShoppingList,
			stopEditingShoppingList,
			addProductToList,
			bulkAddProductToList,
			removeShoppingList,
		},
	};
}
